import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, LogOut, ExternalLink, Sparkles, Loader2 } from "lucide-react";
import { Crest } from "@/components/Crest";
import { useAuth } from "@/context/AuthContext";
import { ImageUpload } from "@/components/ImageUpload";
import {
  listItems, createItem, updateItem, deleteItem,
  getStats, getSettings, updateStats, updateSettings,
  listMessages, deleteMessage, listCompetitions, optimizeArticle,
} from "@/lib/db";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const SPORTS = [["football", "Ποδόσφαιρο"], ["basketball", "Μπάσκετ"]];
const POSITIONS = [
  ["gk", "Τερματοφύλακας"], ["def", "Αμυντικός"], ["mid", "Μέσος"], ["fwd", "Επιθετικός"],
  ["guard", "Γκαρντ"], ["forward", "Φόργουορντ"], ["center", "Σέντερ"],
];
const TIERS = [["gold", "Χρυσός"], ["silver", "Ασημένιος"], ["supporter", "Υποστηρικτής"]];

const CONFIGS = {
  players: {
    label: "Παίκτες",
    columns: [["number", "#"], ["name", "Όνομα"], ["sport", "Άθλημα"], ["position", "Θέση"]],
    fields: [
      { k: "sport", label: "Άθλημα", type: "select", opts: SPORTS },
      { k: "position", label: "Θέση", type: "select", opts: POSITIONS },
      { k: "name", label: "Ονοματεπώνυμο", type: "text" },
      { k: "number", label: "Νούμερο φανέλας", type: "number" },
      { k: "age", label: "Ηλικία", type: "number" },
      { k: "height", label: "Ύψος (π.χ. 1.85μ)", type: "text" },
      { k: "nationality", label: "Εθνικότητα", type: "text" },
      { k: "photo", label: "Φωτογραφία", type: "image" },
      { k: "order", label: "Σειρά", type: "number" },
    ],
  },
  staff: {
    label: "Τεχνικό Επιτελείο",
    columns: [["name", "Όνομα"], ["role", "Ρόλος"], ["sport", "Άθλημα"]],
    fields: [
      { k: "sport", label: "Άθλημα", type: "select", opts: SPORTS },
      { k: "role", label: "Ρόλος", type: "text" },
      { k: "name", label: "Ονοματεπώνυμο", type: "text" },
      { k: "photo", label: "Φωτογραφία", type: "image" },
      { k: "order", label: "Σειρά", type: "number" },
    ],
  },
  news: {
    label: "Νέα",
    columns: [["date", "Ημ/νία"], ["title", "Τίτλος"]],
    fields: [
      { k: "title", label: "Τίτλος", type: "text" },
      { k: "date", label: "Ημερομηνία (ΕΕΕΕ-ΜΜ-ΗΗ)", type: "text" },
      { k: "excerpt", label: "Περίληψη", type: "textarea" },
      { k: "content", label: "Πλήρες κείμενο", type: "textarea", ai: true },
      { k: "image", label: "Εικόνα", type: "image" },
    ],
  },
  matches: {
    label: "Αγώνες & Αποτελέσματα",
    columns: [["date", "Ημ/νία"], ["opponent", "Αντίπαλος"], ["sport", "Άθλημα"], ["competition", "Διοργάνωση"], ["completed", "Κατάσταση"]],
    fields: [
      { k: "sport", label: "Άθλημα", type: "select", opts: SPORTS },
      { k: "opponent", label: "Αντίπαλος", type: "text" },
      { k: "date", label: "Ημερομηνία (ΕΕΕΕ-ΜΜ-ΗΗ)", type: "text" },
      { k: "venue", label: "Γήπεδο", type: "text" },
      { k: "competition", label: "Διοργάνωση / Πρωτάθλημα", type: "dynamic" },
      { k: "home", label: "Έδρα", type: "select", opts: [["true", "Εντός"], ["false", "Εκτός"]], bool: true },
      { k: "completed", label: "Κατάσταση αγώνα", type: "select", opts: [["false", "Προσεχής"], ["true", "Ολοκληρωμένος (με σκορ)"]], bool: true },
      { k: "our_score", label: "Σκορ ομάδας μας", type: "number" },
      { k: "opp_score", label: "Σκορ αντιπάλου", type: "number" },
    ],
  },
  partners: {
    label: "Συνεργάτες",
    columns: [["name", "Όνομα"], ["tier", "Κατηγορία"]],
    fields: [
      { k: "tier", label: "Κατηγορία", type: "select", opts: TIERS },
      { k: "name", label: "Όνομα", type: "text" },
      { k: "description", label: "Περιγραφή", type: "textarea" },
      { k: "logo", label: "Λογότυπο", type: "image" },
      { k: "website", label: "Ιστοσελίδα (URL)", type: "text" },
      { k: "order", label: "Σειρά", type: "number" },
    ],
  },
  competitions: {
    label: "Διοργανώσεις",
    columns: [["name", "Όνομα"], ["sport", "Άθλημα"]],
    fields: [
      { k: "name", label: "Όνομα διοργάνωσης", type: "text" },
      { k: "sport", label: "Άθλημα", type: "select", opts: [["both", "Και τα δύο"], ["football", "Ποδόσφαιρο"], ["basketball", "Μπάσκετ"]] },
      { k: "order", label: "Σειρά", type: "number" },
    ],
  },
};

const inputCls =
  "w-full rounded-lg border border-white/15 bg-royal/60 px-3 py-2 text-ice placeholder-ice/40 outline-none transition-all focus:border-gold focus:ring-2 focus:ring-gold/30";

function labelFor(opts, val) {
  const f = opts?.find((o) => String(o[0]) === String(val));
  return f ? f[1] : val;
}

function ItemManager({ coll }) {
  const cfg = CONFIGS[coll];
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [delId, setDelId] = useState(null);
  const [competitions, setCompetitions] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);

  const load = () => listItems(coll).then((d) => setItems(d)).catch(() => {});
  useEffect(() => { load(); listCompetitions().then(setCompetitions).catch(() => {}); /* eslint-disable-next-line */ }, [coll]);

  const openNew = () => {
    const init = {};
    cfg.fields.forEach((f) => {
      init[f.k] = f.type === "select" ? f.opts[0][0] : "";
    });
    setForm(init); setEditing(null); setOpen(true);
  };

  const openEdit = (item) => {
    const init = {};
    cfg.fields.forEach((f) => { init[f.k] = f.bool ? String(item[f.k]) : (item[f.k] ?? ""); });
    setForm(init); setEditing(item); setOpen(true);
  };

  const save = async () => {
    const payload = {};
    for (const f of cfg.fields) {
      let v = form[f.k];
      if (f.type === "number") v = v === "" ? 0 : Number(v);
      if (f.bool) v = v === "true";
      payload[f.k] = v;
    }
    try {
      if (editing) await updateItem(coll, editing.id, payload);
      else await createItem(coll, payload);
      toast.success("Αποθηκεύτηκε");
      setOpen(false); load();
    } catch (e) {
      toast.error("Αποτυχία αποθήκευσης");
    }
  };

  const doDelete = async () => {
    try {
      await deleteItem(coll, delId);
      toast.success("Διαγράφηκε"); setDelId(null); load();
    } catch { toast.error("Αποτυχία διαγραφής"); }
  };

  const runAi = async (field) => {
    const seed = String(form[field] || form.excerpt || form.title || "").trim();
    if (!seed) { toast.error("Γράψτε λίγο κείμενο, περίληψη ή τίτλο πρώτα"); return; }
    setAiLoading(true);
    try {
      const content = await optimizeArticle(seed, form.title || "");
      if (content) {
        setForm((s) => ({ ...s, [field]: content }));
        toast.success("Το άρθρο δημιουργήθηκε με AI");
      } else { toast.error("Δεν επιστράφηκε κείμενο"); }
    } catch { toast.error("Αποτυχία βελτιστοποίησης με AI"); }
    finally { setAiLoading(false); }
  };

  return (
    <div>
      <div className="mb-5 flex items-center justify-between">
        <h2 className="font-heading text-2xl font-800 text-white">{cfg.label} <span className="text-ice/50">({items.length})</span></h2>
        <button onClick={openNew} data-testid={`add-${coll}`} className="inline-flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 font-display font-700 uppercase tracking-wide text-white transition-all hover:bg-brand/80">
          <Plus size={18} /> Προσθήκη
        </button>
      </div>

      <div className="overflow-x-auto rounded-xl border border-white/10">
        <table className="w-full text-left text-sm">
          <thead className="bg-royal/60 text-ice">
            <tr>
              {cfg.columns.map(([, l]) => <th key={l} className="px-4 py-3 font-display font-700 uppercase tracking-wide">{l}</th>)}
              <th className="px-4 py-3 text-right font-display font-700 uppercase tracking-wide">Ενέργειες</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} className="border-t border-white/5 hover:bg-royal/20" data-testid={`row-${coll}-${it.id}`}>
                {cfg.columns.map(([k]) => {
                  const field = cfg.fields.find((f) => f.k === k);
                  let val = it[k];
                  if (field?.type === "select") val = labelFor(field.opts, field.bool ? String(it[k]) : it[k]);
                  return <td key={k} className="px-4 py-3 text-ice/90">{String(val ?? "")}</td>;
                })}
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-2">
                    <button onClick={() => openEdit(it)} data-testid={`edit-${coll}-${it.id}`} className="rounded-md p-2 text-brand hover:bg-brand/15"><Pencil size={16} /></button>
                    <button onClick={() => setDelId(it.id)} data-testid={`delete-${coll}-${it.id}`} className="rounded-md p-2 text-red-400 hover:bg-red-400/15"><Trash2 size={16} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={cfg.columns.length + 1} className="px-4 py-8 text-center text-ice/50">Δεν υπάρχουν εγγραφές</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto border-white/10 bg-navy text-ice sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-heading text-2xl text-white">{editing ? "Επεξεργασία" : "Προσθήκη"} — {cfg.label}</DialogTitle><DialogDescription className="text-ice/60">Συμπληρώστε τα πεδία και πατήστε Αποθήκευση.</DialogDescription></DialogHeader>
          <div className="space-y-4 py-2">
            {cfg.fields.map((f) => (
              <div key={f.k}>
                <label className="mb-1 block text-sm font-600 text-ice">{f.label}</label>
                {f.type === "image" ? (
                  <ImageUpload value={form[f.k] ?? ""} onChange={(url) => setForm((s) => ({ ...s, [f.k]: url }))} testid={`field-${f.k}`} />
                ) : f.type === "dynamic" ? (
                  <Select value={String(form[f.k] ?? "")} onValueChange={(v) => setForm((s) => ({ ...s, [f.k]: v }))}>
                    <SelectTrigger className={inputCls} data-testid={`field-${f.k}`}><SelectValue placeholder="Επιλέξτε διοργάνωση" /></SelectTrigger>
                    <SelectContent className="border-white/15 bg-royal text-ice">
                      {competitions
                        .filter((c) => !form.sport || c.sport === "both" || c.sport === form.sport || c.name === form[f.k])
                        .map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : f.type === "textarea" ? (
                  <div>
                    <textarea rows={3} className={inputCls} value={form[f.k] ?? ""} onChange={(e) => setForm((s) => ({ ...s, [f.k]: e.target.value }))} data-testid={`field-${f.k}`} />
                    {f.ai && (
                      <button
                        type="button"
                        onClick={() => runAi(f.k)}
                        disabled={aiLoading}
                        data-testid="ai-optimize-btn"
                        className="mt-2 inline-flex items-center gap-2 rounded-lg border border-gold/50 px-3 py-2 text-sm font-600 text-gold transition-colors hover:bg-gold/10 disabled:opacity-60"
                      >
                        {aiLoading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                        {aiLoading ? "Δημιουργία..." : "Βελτιστοποίηση με AI"}
                      </button>
                    )}
                  </div>
                ) : f.type === "select" ? (
                  <Select value={String(form[f.k] ?? "")} onValueChange={(v) => setForm((s) => ({ ...s, [f.k]: v }))}>
                    <SelectTrigger className={inputCls} data-testid={`field-${f.k}`}><SelectValue /></SelectTrigger>
                    <SelectContent className="border-white/15 bg-royal text-ice">
                      {f.opts.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <input type={f.type === "number" ? "number" : "text"} className={inputCls} value={form[f.k] ?? ""} onChange={(e) => setForm((s) => ({ ...s, [f.k]: e.target.value }))} data-testid={`field-${f.k}`} />
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="rounded-full border border-white/20 px-5 py-2.5 font-600 text-ice hover:bg-white/5">Άκυρο</button>
            <button onClick={save} data-testid="save-item" className="rounded-full bg-gold px-6 py-2.5 font-display font-700 uppercase tracking-wide text-navy hover:bg-gold/85">Αποθήκευση</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delId} onOpenChange={(o) => !o && setDelId(null)}>
        <AlertDialogContent className="border-white/10 bg-navy text-ice">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Διαγραφή εγγραφής;</AlertDialogTitle>
            <AlertDialogDescription className="text-ice/70">Η ενέργεια αυτή δεν αναιρείται.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-white/20 bg-transparent text-ice hover:bg-white/5">Άκυρο</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="confirm-delete" className="bg-red-500 text-white hover:bg-red-600">Διαγραφή</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function MetaEditor({ endpoint, fields, title, testid }) {
  const [form, setForm] = useState({});

  useEffect(() => {
    (endpoint === "stats" ? getStats() : getSettings()).then((d) => setForm(d)).catch(() => {});
    /* eslint-disable-next-line */
  }, []);

  const save = async () => {
    const payload = { ...form };
    delete payload.id;
    fields.forEach((f) => { if (f.type === "number") payload[f.k] = Number(payload[f.k] ?? 0); });
    try {
      if (endpoint === "stats") await updateStats(payload);
      else await updateSettings(payload);
      toast.success("Αποθηκεύτηκε");
    } catch { toast.error("Αποτυχία αποθήκευσης"); }
  };

  return (
    <div>
      <h2 className="mb-5 font-heading text-2xl font-800 text-white">{title}</h2>
      <div className="grid gap-4 sm:grid-cols-2">
        {fields.map((f) => (
          <div key={f.k} className={f.type === "textarea" ? "sm:col-span-2" : ""}>
            <label className="mb-1 block text-sm font-600 text-ice">{f.label}</label>
            {f.type === "textarea" ? (
              <textarea rows={3} className={inputCls} value={form[f.k] ?? ""} onChange={(e) => setForm((s) => ({ ...s, [f.k]: e.target.value }))} data-testid={`meta-${f.k}`} />
            ) : (
              <input type={f.type === "number" ? "number" : "text"} className={inputCls} value={form[f.k] ?? ""} onChange={(e) => setForm((s) => ({ ...s, [f.k]: e.target.value }))} data-testid={`meta-${f.k}`} />
            )}
          </div>
        ))}
      </div>
      <button onClick={save} data-testid={testid} className="mt-6 rounded-full bg-gold px-8 py-3 font-display font-700 uppercase tracking-wide text-navy hover:bg-gold/85">Αποθήκευση</button>
    </div>
  );
}

function MessagesView() {
  const [msgs, setMsgs] = useState([]);
  const load = () => listMessages().then((d) => setMsgs(d)).catch(() => {});
  useEffect(() => { load(); }, []);
  const del = async (id) => { await deleteMessage(id); toast.success("Διαγράφηκε"); load(); };

  return (
    <div>
      <h2 className="mb-5 font-heading text-2xl font-800 text-white">Μηνύματα <span className="text-ice/50">({msgs.length})</span></h2>
      <div className="space-y-4">
        {msgs.map((m) => (
          <div key={m.id} className="rounded-xl border border-white/10 bg-royal/30 p-5" data-testid={`message-${m.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="font-display text-lg font-800 uppercase text-white">{m.name} <span className="ml-2 rounded-full bg-brand/20 px-2 py-0.5 text-xs text-brand">{m.subject}</span></p>
                <p className="text-sm text-ice/70">{m.email}{m.phone ? ` · ${m.phone}` : ""}</p>
              </div>
              <button onClick={() => del(m.id)} className="rounded-md p-2 text-red-400 hover:bg-red-400/15"><Trash2 size={16} /></button>
            </div>
            <p className="mt-3 whitespace-pre-wrap text-ice/90">{m.message}</p>
          </div>
        ))}
        {msgs.length === 0 && <p className="text-ice/50">Δεν υπάρχουν μηνύματα.</p>}
      </div>
    </div>
  );
}

const STATS_FIELDS = [
  { k: "years", label: "Έτη Ιστορίας", type: "number" },
  { k: "athletes", label: "Αθλητές", type: "number" },
  { k: "titles", label: "Τίτλοι", type: "number" },
  { k: "departments", label: "Τμήματα", type: "number" },
];
const SETTINGS_FIELDS = [
  { k: "address", label: "Διεύθυνση" }, { k: "phone", label: "Τηλέφωνο" }, { k: "email", label: "Email" },
  { k: "training_hours", label: "Ώρες προπόνησης" },
  { k: "facebook", label: "Facebook URL" }, { k: "instagram", label: "Instagram URL" },
  { k: "youtube", label: "YouTube URL" }, { k: "tiktok", label: "TikTok URL" },
  { k: "football_venue_name", label: "Γήπεδο Ποδοσφαίρου - Όνομα" }, { k: "football_venue_address", label: "Γήπεδο Ποδοσφαίρου - Διεύθυνση" },
  { k: "basketball_venue_name", label: "Κλειστό Μπάσκετ - Όνομα" }, { k: "basketball_venue_address", label: "Κλειστό Μπάσκετ - Διεύθυνση" },
  { k: "club_description", label: "Περιγραφή συλλόγου", type: "textarea" },
];

const TABS = [
  { key: "players", label: "Παίκτες" },
  { key: "staff", label: "Επιτελείο" },
  { key: "news", label: "Νέα" },
  { key: "competitions", label: "Διοργανώσεις" },
  { key: "matches", label: "Αγώνες & Αποτελέσματα" },
  { key: "partners", label: "Συνεργάτες" },
  { key: "stats", label: "Αριθμοί" },
  { key: "settings", label: "Ρυθμίσεις" },
  { key: "messages", label: "Μηνύματα" },
];

export default function Admin() {
  const { logout, user } = useAuth();
  const [tab, setTab] = useState("players");

  return (
    <div className="min-h-screen bg-navy">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-navy/95 backdrop-blur-xl">
        <div className="club-container flex items-center justify-between py-4">
          <Link to="/" className="flex items-center gap-3">
            <Crest size={44} />
            <div>
              <p className="font-display text-lg font-800 uppercase leading-none text-white">Πίνακας Διαχείρισης</p>
              <p className="text-xs text-ice/60">{user?.email}</p>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/" className="hidden items-center gap-1.5 text-sm text-ice/70 hover:text-gold sm:inline-flex"><ExternalLink size={15} /> Ιστοσελίδα</Link>
            <button onClick={logout} data-testid="logout-btn" className="inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 font-600 text-ice hover:border-red-400 hover:text-red-400">
              <LogOut size={16} /> Έξοδος
            </button>
          </div>
        </div>
      </header>

      <div className="club-container flex flex-wrap gap-2 py-6">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`admin-tab-${t.key}`}
            className={`rounded-full px-5 py-2 font-display font-700 uppercase tracking-wide transition-all ${
              tab === t.key ? "bg-brand text-white" : "border border-white/15 text-ice hover:border-gold hover:text-gold"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="club-container pb-24">
        {["players", "staff", "news", "competitions", "matches", "partners"].includes(tab) && <ItemManager coll={tab} key={tab} />}
        {tab === "stats" && <MetaEditor endpoint="stats" fields={STATS_FIELDS} title="Ο σύλλογος σε αριθμούς" testid="save-stats" />}
        {tab === "settings" && <MetaEditor endpoint="settings" fields={SETTINGS_FIELDS} title="Ρυθμίσεις & Στοιχεία επικοινωνίας" testid="save-settings" />}
        {tab === "messages" && <MessagesView />}
      </div>
    </div>
  );
}
